import { useState, useEffect } from 'react';
import { Search, X, Loader2, TrendingUp, DollarSign } from 'lucide-react';
import { fetchActiveDeals } from '../../services/salesCoachContextService';

interface DealSelectorProps {
  onSelect: (deal: any) => void;
  onClose: () => void;
}

export function DealSelector({ onSelect, onClose }: DealSelectorProps) {
  const [deals, setDeals] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await fetchActiveDeals();
      setDeals(data);
      setFiltered(data);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(deals);
      return;
    }
    const q = search.toLowerCase();
    setFiltered(
      deals.filter(
        (d) =>
          d.company_name?.toLowerCase().includes(q) ||
          d.dm_full_name?.toLowerCase().includes(q) ||
          d.pipeline_stage?.toLowerCase().includes(q)
      )
    );
  }, [search, deals]);

  const formatStage = (stage: string) => {
    return stage?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Unknown';
  };

  const formatValue = (value: number | null) => {
    if (!value) return 'No value set';
    return `$${Number(value).toLocaleString()}`;
  };

  const getStageColor = (stage: string) => {
    const s = stage?.toLowerCase() || '';
    if (s.includes('qualif')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (s.includes('proposal') || s.includes('demo')) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (s.includes('negotiat')) return 'bg-orange-50 text-orange-700 border-orange-200';
    if (s.includes('close') || s.includes('won')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getTempColor = (temp: string) => {
    const t = temp?.toLowerCase() || '';
    if (t.includes('hot')) return 'text-red-500';
    if (t.includes('warm')) return 'text-amber-500';
    return 'text-blue-400';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Select a Deal</h3>
            <p className="text-sm text-gray-500 mt-0.5">Choose a deal to get strategy advice</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by company, contact, or stage..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all text-sm"
              autoFocus
            />
          </div>
        </div>

        <div className="px-4 pb-4 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-[#1875FF] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">
                {search ? 'No deals match your search' : 'No active deals found'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((deal) => (
                <button
                  key={deal.deal_id}
                  onClick={() => onSelect(deal)}
                  className="w-full flex items-center gap-4 p-3.5 rounded-xl border border-gray-100 hover:border-[#1875FF]/40 hover:bg-[#1875FF]/5 transition-all text-left group"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center">
                    <DollarSign size={22} className={getTempColor(deal.ovrall_lead_temp)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-sm truncate group-hover:text-[#1875FF] transition-colors">
                      {deal.company_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {deal.dm_full_name}{deal.dm_title ? ` - ${deal.dm_title}` : ''}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getStageColor(deal.pipeline_stage)}`}>
                        {formatStage(deal.pipeline_stage)}
                      </span>
                      <span className="text-xs text-gray-500 font-medium">
                        {formatValue(deal.deal_value)}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
